import React, { useState } from "react";
import {
  Mail,
  Phone,
  MessageCircle,
  Send,
  MapPin,
  CheckCircle2,
} from "lucide-react";

const EMAIL = "sparknextpathways@gmail.com";
const PHONE = "+91 76195 52312";
const PHONE_RAW = "+917619552312";
const WHATSAPP = "https://wa.me/917619552312";

export const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleMailto = (e) => {
    e.preventDefault();

    const subject = encodeURIComponent(
      `Inquiry from ${form.name || "website visitor"}`
    );

    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.organization ? `Organization: ${form.organization}` : null,
      "",
      "Message:",
      form.message,
    ].filter(Boolean);

    const body = encodeURIComponent(bodyLines.join("\n"));
    const href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;

    window.location.href = href;
    setSent(true);
  };

  return (
    <main data-testid="contact-page">
      {/* HEADER */}
      <section
        data-testid="contact-header-section"
        className="bg-white border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-5">
              Contact
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Let's build the future of financial literacy together.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              Whether you represent a school, a CSR foundation, or simply share
              our mission — we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT BODY */}
      <section
        data-testid="contact-body-section"
        className="py-16 md:py-24 bg-slate-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* CONTACT DETAILS */}
            <aside
              data-testid="contact-details-panel"
              className="lg:col-span-5 space-y-4"
            >
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-card"
                className="group block bg-white border border-slate-200 rounded-xl p-7 hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-slate-900 text-white flex items-center justify-center group-hover:bg-amber-500 group-hover:text-slate-900 transition-colors shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">
                      Email
                    </p>
                    <p className="mt-1 text-base font-semibold text-slate-900 break-all">
                      {EMAIL}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      For partnerships, schools, and general inquiries.
                    </p>
                  </div>
                </div>
              </a>

              <a
                href={`tel:${PHONE_RAW}`}
                data-testid="contact-phone-card"
                className="group block bg-white border border-slate-200 rounded-xl p-7 hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-slate-900 text-white flex items-center justify-center group-hover:bg-amber-500 group-hover:text-slate-900 transition-colors shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">
                      Phone
                    </p>
                    <p className="mt-1 text-base font-semibold text-slate-900">
                      {PHONE}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Tap to call directly.
                    </p>
                  </div>
                </div>
              </a>

              <a
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="group block bg-white border border-slate-200 rounded-xl p-7 hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-amber-500 text-slate-900 flex items-center justify-center shrink-0">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">
                      WhatsApp
                    </p>
                    <p className="mt-1 text-base font-semibold text-slate-900">
                      Chat on WhatsApp
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Usually the fastest way to reach us.
                    </p>
                  </div>
                </div>
              </a>

              <div
                data-testid="contact-location-card"
                className="bg-white border border-slate-200 rounded-xl p-7"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-slate-100 text-slate-900 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">
                      Registered Office
                    </p>
                    <p className="mt-1 text-base font-semibold text-slate-900">
                      SparkNext Pathways Private Limited
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Registered in India.
                    </p>
                  </div>
                </div>
              </div>
            </aside>

            {/* FORM */}
            <div className="lg:col-span-7">
              <div
                data-testid="contact-form-wrapper"
                className="bg-white border border-slate-200 rounded-xl p-8 md:p-10"
              >
                <div className="mb-6">
                  <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                    Send us a message
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Fill out the form and your email client will open with your
                    message pre-filled, addressed to us.
                  </p>
                </div>

                <form
                  data-testid="contact-form"
                  onSubmit={handleMailto}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field
                      id="name"
                      label="Full Name"
                      required
                      placeholder="Your full name"
                      value={form.name}
                      onChange={handleChange}
                      testId="contact-input-name"
                    />
                    <Field
                      id="email"
                      type="email"
                      label="Email"
                      required
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      testId="contact-input-email"
                    />
                  </div>

                  <Field
                    id="organization"
                    label="Organization"
                    placeholder="School, foundation, or company (optional)"
                    value={form.organization}
                    onChange={handleChange}
                    testId="contact-input-organization"
                  />

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-slate-900 mb-1.5"
                    >
                      Message <span className="text-amber-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={handleChange}
                      data-testid="contact-input-message"
                      placeholder="Tell us a bit about your organization and how we can help…"
                      className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-colors"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                    <button
                      type="submit"
                      data-testid="contact-form-submit"
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white px-6 py-3.5 text-base font-semibold hover:bg-slate-800 transition-colors"
                    >
                      Send via Email
                      <Send className="w-4 h-4" />
                    </button>
                    <a
                      href={WHATSAPP}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-form-whatsapp"
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 text-slate-900 px-6 py-3.5 text-base font-semibold hover:bg-amber-400 transition-colors"
                    >
                      Message on WhatsApp
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  </div>

                  {sent && (
                    <div
                      data-testid="contact-form-success"
                      className="mt-2 flex items-start gap-3 rounded-md bg-slate-900 text-white px-4 py-3 text-sm"
                    >
                      <CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <span>
                        Your email client should have opened with the message
                        ready to send. If not, email us at{" "}
                        <a
                          className="underline text-amber-400"
                          href={`mailto:${EMAIL}`}
                        >
                          {EMAIL}
                        </a>
                        .
                      </span>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const Field = ({
  id,
  label,
  type = "text",
  required = false,
  placeholder,
  value,
  onChange,
  testId,
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-semibold text-slate-900 mb-1.5"
    >
      {label}
      {required && <span className="text-amber-500"> *</span>}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-colors"
    />
  </div>
);

export default ContactPage;
